import React, { useState, useEffect } from "react";
import config from "../../config/env";
import { externalRagApi } from "@/api/externalRagApi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { notificationService } from "@/services/notificationService";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  createTrainingJob,
  getTrainingJobs,
  getAllModels,
  deployModel,
  getPipelines,
  updatePipelineStatus,
  type TrainingJob,
  type MLModel,
  type DetectionPipeline,
} from "@/api/modelStudioApi";
import { getServices, type Service } from "@/api/api";
import {
  ragApi,
  incidentApi,
  CATEGORY_LABELS,
  type KnowledgeBase,
  type RAGAgent,
  type IncidentCategoryResponse,
} from "@/api/incidentApi";
import {
  Brain,
  Play,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Zap,
  Rocket,
  Database,
  Bot,
  Upload,
  FileText,
  BookOpen,
  Cloud,
  BarChart3,
  AlertCircle,
  Check,
} from "lucide-react";
import DocumentManager from "@/components/DocumentManager";
import ExternalRAGAgentForm from "@/pages/ModelStudio/Components/ExternalRAGAgentForm";
import ExternalRAGStats from "@/components/ExternalRAGStats";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const ModelStudio: React.FC = () => {
  const [trainingJobs, setTrainingJobs] = useState<TrainingJob[]>([]);
  const [models, setModels] = useState<MLModel[]>([]);
  const [pipelines, setPipelines] = useState<DetectionPipeline[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [ragAgents, setRAGAgents] = useState<RAGAgent[]>([]);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [categories, setCategories] = useState<IncidentCategoryResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showTrainingDialog, setShowTrainingDialog] = useState<boolean>(false);
  const [showDeployDialog, setShowDeployDialog] = useState<boolean>(false);
  const [showRAGAgentDialog, setShowRAGAgentDialog] = useState<boolean>(false);
  const [showKnowledgeBaseDialog, setShowKnowledgeBaseDialog] = useState<boolean>(false);
  const [showDocumentManager, setShowDocumentManager] = useState<boolean>(false);
  const [showExternalRAGForm, setShowExternalRAGForm] = useState<boolean>(false);
  const [showExternalRAGStats, setShowExternalRAGStats] = useState<boolean>(false);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState<KnowledgeBase | null>(null);
  const [selectedModel, setSelectedModel] = useState<MLModel | null>(null);
  const [activeTab, setActiveTab] = useState<string>("models");

  // Training form state
  const [trainingForm, setTrainingForm] = useState({
    serviceName: "",
    metricName: "avg_response_time",
    ensembleMethod: "lscp",
    epochs: 50,
    trainingDataPoints: 200,
  });

  // Deploy form state
  const [deployForm, setDeployForm] = useState({
    versionId: "",
    pipelineName: "",
  });

  // RAG Agent form state
  const [ragAgentForm, setRAGAgentForm] = useState({
    name: "",
    description: "",
    categories: [] as string[],
    knowledge_bases: [] as string[],
    model_provider: "openai",
    model_name: "gpt-4o",
    system_prompt:
      "You are an expert IT incident response specialist. Analyze incidents and provide structured, actionable recommendations based on established SOPs and knowledge base information.",
    max_tokens: 2000,
    temperature: 0.1,
    confidence_threshold: 0.7,
    auto_assign: false,
    learning_enabled: true,
  });

  // Detail view states
  const [selectedRAGAgent, setSelectedRAGAgent] = useState<RAGAgent | null>(null);
  const [showKnowledgeBaseDetail, setShowKnowledgeBaseDetail] = useState<boolean>(false);
  const [showRAGAgentDetail, setShowRAGAgentDetail] = useState<boolean>(false);
  const [showAddProcedure, setShowAddProcedure] = useState<boolean>(false);
  const [agentStats, setAgentStats] = useState<Record<string, any>>({});
  const [editingKB, setEditingKB] = useState<boolean>(false);
  const [editingAgent, setEditingAgent] = useState<boolean>(false);
  const [testingLLM, setTestingLLM] = useState<boolean>(false);
  const [testPrompt, setTestPrompt] = useState<string>("");
  const [testResult, setTestResult] = useState<string>("");
  const [createExternalRAGAgentLoading, setCreateExternalRAGAgentLoading] = useState(false);

  // New procedure form
  const [newProcedure, setNewProcedure] = useState({
    step: 1,
    title: "",
    actions: [""] as string[],
  });

  // Knowledge Base form state
  const [knowledgeBaseForm, setKnowledgeBaseForm] = useState({
    name: "",
    description: "",
    category: "",
    content_type: "structured",
    structured_content: {} as Record<string, any>,
    tags: [] as string[],
    created_by: "admin@ns2-nimbusmind.com",
  });

  // Knowledge Base form validation state
  const [kbFormErrors, setKbFormErrors] = useState<Record<string, string>>({});
  const [kbUploadedFile, setKbUploadedFile] = useState<File | null>(null);
  const [kbFileError, setKbFileError] = useState<string>("");
  const [kbTouched, setKbTouched] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchData();
    // const interval = setInterval(fetchData, 5000); // Refresh every 5 seconds
    // return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [
        jobsData,
        modelsData,
        pipelinesData,
        servicesData,
        ragAgentsData,
        knowledgeBasesData,
        categoriesData,
      ] = await Promise.all([
        getTrainingJobs(),
        getAllModels(),
        getPipelines(),
        getServices(),
        ragApi.getRAGAgents().catch(() => []),
        ragApi.getKnowledgeBases().catch(() => []),
        incidentApi.getIncidentCategories().catch(() => []),
      ]);

      setTrainingJobs(jobsData);
      setModels(modelsData);
      setPipelines(pipelinesData);
      setServices(servicesData);
      setRAGAgents(ragAgentsData);
      setKnowledgeBases(knowledgeBasesData);
      setCategories(categoriesData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching model studio data:", error);
      setLoading(false);
    }
  };

  const handleCreateTrainingJob = async () => {
    try {
      await createTrainingJob(
        trainingForm.serviceName,
        trainingForm.metricName,
        trainingForm.ensembleMethod,
        trainingForm.epochs,
        trainingForm.trainingDataPoints
      );
      setShowTrainingDialog(false);

      // Switch to ML Models tab
      setActiveTab("models");

      fetchData();
    } catch (error) {
      console.error("Error creating training job:", error);
    }
  };

  const handleDeployModel = async () => {
    try {
      await deployModel(deployForm.versionId, deployForm.pipelineName);
      setShowDeployDialog(false);
      fetchData();
    } catch (error) {
      console.error("Error deploying model:", error);
    }
  };

  const handleTogglePipeline = async (pipelineId: string, currentStatus: boolean) => {
    try {
      await updatePipelineStatus(pipelineId, !currentStatus);
      fetchData();
    } catch (error) {
      console.error("Error toggling pipeline:", error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "failed":
        return <XCircle className="w-5 h-5 text-red-500" />;
      case "training":
        return <Play className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "training":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Helper function to get category name from category code
  const getCategoryName = (categoryCode: string): string => {
    const category = categories.find((cat) => cat.category === categoryCode);
    return category?.name || CATEGORY_LABELS[categoryCode] || categoryCode;
  };

  const metrics = ["avg_response_time", "request_count", "error_rate", "cpu_usage", "memory_usage"];
  const ensembleMethods = [
    { value: "lscp", label: "LSCP (F1: 0.86)" },
    { value: "isolation_forest", label: "Isolation Forest (F1: 0.84)" },
    { value: "ocsvm", label: "One-Class SVM (F1: 0.82)" },
  ];

  // RAG Agent functions
  const handleCreateRAGAgent = async () => {
    try {
      await ragApi.createRAGAgent(ragAgentForm);
      setShowRAGAgentDialog(false);
      setRAGAgentForm({
        name: "",
        description: "",
        categories: [],
        knowledge_bases: [],
        model_provider: "openai",
        model_name: "gpt-4o",
        system_prompt:
          "You are an expert IT incident response specialist. Analyze incidents and provide structured, actionable recommendations based on established SOPs and knowledge base information.",
        max_tokens: 2000,
        temperature: 0.1,
        confidence_threshold: 0.7,
        auto_assign: false,
        learning_enabled: true,
      });

      // Switch to RAG Agents tab
      setActiveTab("rag");

      fetchData();
    } catch (error) {
      console.error("Error creating RAG agent:", error);
    }
  };

  // Validation functions for Knowledge Base form
  const validateKbName = (value: string): string => {
    if (!value.trim()) {
      return "Name is required";
    }
    if (value.trim().length < 3) {
      return "Name must be at least 3 characters";
    }
    if (value.length > 100) {
      return "Name must not exceed 100 characters";
    }
    return "";
  };

  const validateKbDescription = (value: string): string => {
    if (!value.trim()) {
      return "Description is required";
    }
    if (value.trim().length < 10) {
      return "Description must be at least 10 characters";
    }
    if (value.length > 500) {
      return "Description must not exceed 500 characters";
    }
    return "";
  };

  const validateKbCategory = (value: string): string => {
    if (!value) {
      return "Category is required";
    }
    return "";
  };

  const validateKnowledgeBaseForm = (): boolean => {
    const errors: Record<string, string> = {};

    const nameError = validateKbName(knowledgeBaseForm.name);
    if (nameError) errors.name = nameError;

    const descError = validateKbDescription(knowledgeBaseForm.description);
    if (descError) errors.description = descError;

    const categoryError = validateKbCategory(knowledgeBaseForm.category);
    if (categoryError) errors.category = categoryError;

    // Validate file upload for document content type
    if (knowledgeBaseForm.content_type === "document" && !kbUploadedFile) {
      errors.file = "Please upload a document file";
    }

    setKbFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleKbFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setKbFileError("");

    if (!file) {
      setKbUploadedFile(null);
      if (kbFormErrors.file) {
        const newErrors = { ...kbFormErrors };
        delete newErrors.file;
        setKbFormErrors(newErrors);
      }
      return;
    }

    // File size validation (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      setKbFileError("File size must not exceed 10MB");
      setKbUploadedFile(null);
      event.target.value = "";
      return;
    }

    // File format validation
    const allowedFormats = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
      "text/markdown",
      "application/json",
    ];

    const allowedExtensions = [".pdf", ".doc", ".docx", ".txt", ".md", ".json"];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf("."));

    if (!allowedFormats.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      setKbFileError("Invalid file format. Allowed: PDF, DOC, DOCX, TXT, MD, JSON");
      setKbUploadedFile(null);
      event.target.value = "";
      return;
    }

    // File uploaded successfully
    setKbUploadedFile(file);
    // Clear file error if exists
    if (kbFormErrors.file) {
      const newErrors = { ...kbFormErrors };
      delete newErrors.file;
      setKbFormErrors(newErrors);
    }
  };

  const handleKbFieldChange = (field: string, value: string) => {
    setKnowledgeBaseForm({ ...knowledgeBaseForm, [field]: value });

    // Mark field as touched
    setKbTouched({ ...kbTouched, [field]: true });

    // Real-time validation for touched fields
    if (kbTouched[field] || kbFormErrors[field]) {
      const newErrors = { ...kbFormErrors };

      if (field === "name") {
        const error = validateKbName(value);
        if (error) {
          newErrors.name = error;
        } else {
          delete newErrors.name;
        }
      } else if (field === "description") {
        const error = validateKbDescription(value);
        if (error) {
          newErrors.description = error;
        } else {
          delete newErrors.description;
        }
      } else if (field === "category") {
        const error = validateKbCategory(value);
        if (error) {
          newErrors.category = error;
        } else {
          delete newErrors.category;
        }
      }

      setKbFormErrors(newErrors);
    }
  };

  const handleCreateKnowledgeBase = async () => {
    // Mark all fields as touched
    setKbTouched({
      name: true,
      description: true,
      category: true,
      content_type: true,
    });

    // Validate form before submission
    if (!validateKnowledgeBaseForm()) {
      notificationService.validationError(
        "Please fix validation errors",
        Object.keys(kbFormErrors)
      );
      return;
    }

    try {
      await ragApi.createKnowledgeBase(knowledgeBaseForm);

      // Show success notification
      notificationService.success(
        "Knowledge Base Created",
        `"${knowledgeBaseForm.name}" has been created successfully`
      );

      setShowKnowledgeBaseDialog(false);

      // Reset form
      resetKnowledgeBaseForm();

      // Switch to Knowledge Bases tab
      setActiveTab("knowledge");

      fetchData();
    } catch (error: any) {
      console.error("Error creating knowledge base:", error);

      // Show error notification
      notificationService.error(
        "Failed to Create Knowledge Base",
        error?.message || "An error occurred while creating the knowledge base. Please try again."
      );
    }
  };

  const resetKnowledgeBaseForm = () => {
    setKnowledgeBaseForm({
      name: "",
      description: "",
      category: "",
      content_type: "structured",
      structured_content: {},
      tags: [],
      created_by: "admin@ns2-nimbusmind.com",
    });
    setKbFormErrors({});
    setKbUploadedFile(null);
    setKbFileError("");
    setKbTouched({});
  };

  const isKbFormValid = (): boolean => {
    return (
      knowledgeBaseForm.name.trim().length >= 3 &&
      knowledgeBaseForm.name.length <= 100 &&
      knowledgeBaseForm.description.trim().length >= 10 &&
      knowledgeBaseForm.description.length <= 500 &&
      knowledgeBaseForm.category !== "" &&
      (knowledgeBaseForm.content_type !== "document" || kbUploadedFile !== null)
    );
  };

  const handleCreateMockRAGData = async () => {
    try {
      await Promise.all([ragApi.createMockKnowledgeBases(), ragApi.createMockRAGAgents()]);
      fetchData();
    } catch (error) {
      console.error("Error creating mock RAG data:", error);
    }
  };

  // Knowledge Base detail functions
  const handleViewKnowledgeBase = async (kb: KnowledgeBase) => {
    try {
      console.log("Clicking on Knowledge Base:", kb.name, kb.id);
      setSelectedKnowledgeBase(kb); // Set the basic KB first
      setShowDocumentManager(true); // Show document manager

      // Then fetch detailed information
      const details = await ragApi.getKnowledgeBaseDetails(kb.id);
      console.log("KB Details received:", details);
      setSelectedKnowledgeBase(details);
    } catch (error) {
      console.error("Error fetching knowledge base details:", error);
      // Still show the modal with basic information
      setSelectedKnowledgeBase(kb);
      setShowKnowledgeBaseDetail(true);
    }
  };

  const handleUpdateKnowledgeBase = async (updates: Partial<KnowledgeBase>) => {
    if (!selectedKnowledgeBase) return;
    try {
      await ragApi.updateKnowledgeBase(selectedKnowledgeBase.id, updates);
      // Refresh knowledge base details
      const updatedDetails = await ragApi.getKnowledgeBaseDetails(selectedKnowledgeBase.id);
      setSelectedKnowledgeBase(updatedDetails);
      // Refresh the list
      fetchData();
    } catch (error) {
      console.error("Error updating knowledge base:", error);
    }
  };

  const handleAddProcedure = async () => {
    if (!selectedKnowledgeBase) return;
    try {
      await ragApi.addProcedureToKnowledgeBase(selectedKnowledgeBase.id, newProcedure);
      setNewProcedure({ step: 1, title: "", actions: [""] });
      setShowAddProcedure(false);
      // Refresh the knowledge base details
      const updatedDetails = await ragApi.getKnowledgeBaseDetails(selectedKnowledgeBase.id);
      setSelectedKnowledgeBase(updatedDetails);
    } catch (error) {
      console.error("Error adding procedure:", error);
    }
  };

  // RAG Agent detail functions
  const handleViewRAGAgent = async (agent: RAGAgent) => {
    try {
      console.log("Clicking on RAG Agent:", agent.name, agent.id);
      setSelectedRAGAgent(agent); // Set the basic agent first
      setShowRAGAgentDetail(true); // Show modal immediately

      // Then fetch detailed statistics
      const details = await ragApi.getRAGAgentDetails(agent.id);
      console.log("Agent Details received:", details);
      setSelectedRAGAgent(details);
      setAgentStats(details.statistics || {});
    } catch (error) {
      console.error("Error fetching RAG agent details:", error);
      // Still show the modal with basic information
      setSelectedRAGAgent(agent);
      setAgentStats({
        incidents_processed: agent.incidents_processed || 0,
        success_rate: agent.success_rate || 0,
        total_recommendations: 0,
        learning_events: 0,
      });
      setShowRAGAgentDetail(true);
    }
  };

  const handleUpdateRAGAgent = async (updates: Partial<RAGAgent>) => {
    if (!selectedRAGAgent) return;
    try {
      await ragApi.updateRAGAgent(selectedRAGAgent.id, updates);
      // Refresh agent details
      const updatedDetails = await ragApi.getRAGAgentDetails(selectedRAGAgent.id);
      setSelectedRAGAgent(updatedDetails);
      fetchData(); // Refresh the list
    } catch (error) {
      console.error("Error updating RAG agent:", error);
    }
  };

  const handleTestLLM = async () => {
    if (!testPrompt.trim() || !selectedRAGAgent) return;

    setTestingLLM(true);
    try {
      // For now, simulate a test - in real implementation you'd call an endpoint
      const testResponse = `**Test Response from ${selectedRAGAgent.model_provider}/${selectedRAGAgent.model_name}:**

Based on your prompt: "${testPrompt}"

This is a simulated response showing how the LLM would handle this input with current settings:
- Temperature: ${selectedRAGAgent.temperature}
- Max Tokens: ${selectedRAGAgent.max_tokens}
- Model: ${selectedRAGAgent.model_provider}/${selectedRAGAgent.model_name}

The response demonstrates the agent's reasoning capabilities and adherence to the system prompt configuration.`;

      setTestResult(testResponse);
    } catch (error) {
      console.error("Error testing LLM:", error);
      setTestResult("Error: Failed to test LLM. Please check configuration and try again.");
    } finally {
      setTestingLLM(false);
    }
  };

  // External RAG Agent handlers
  const handleCreateExternalRAGAgent = async (agentData: Partial<RAGAgent>) => {
    try {
      setCreateExternalRAGAgentLoading(true);
      const res = await externalRagApi.createAgent(agentData);
      setCreateExternalRAGAgentLoading(false);
      setShowExternalRAGForm(false);
      alert("External RAG agent created successfully!");
      setShowExternalRAGStats(true);
    } catch (error) {
      console.error("Error creating external RAG agent:", error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="model-studio-loading">
        <Brain className="w-12 h-12 animate-pulse text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="model-studio">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Model Studio</h2>
          <p className="text-gray-600 mt-1">
            Train, version, and deploy ML models on new data streams
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowTrainingDialog(true)} data-testid="new-training-btn">
            <Brain className="w-4 h-4 mr-2" />
            New Training Job
          </Button>
          <Button onClick={() => setShowRAGAgentDialog(true)} variant="outline">
            <Bot className="w-4 h-4 mr-2" />
            New RAG Agent
          </Button>
          <Button onClick={() => setShowKnowledgeBaseDialog(true)} variant="outline">
            <Database className="w-4 h-4 mr-2" />
            New Knowledge Base
          </Button>
          {/* commented this as part of navigation bug fix
          <Button onClick={handleCreateMockRAGData} variant="outline">
            <Zap className="w-4 h-4 mr-2" />
            Create Mock RAG Data
          </Button> */}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab("models")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "models"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            ML Models
          </button>
          <button
            onClick={() => setActiveTab("rag")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "rag"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            RAG Agents
          </button>
          <button
            onClick={() => setActiveTab("knowledge")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "knowledge"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Knowledge Bases
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "models" && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Training Jobs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{trainingJobs.length}</div>
                <p className="text-xs text-gray-500 mt-1">
                  {trainingJobs.filter((j: TrainingJob) => j.status === "training").length} active
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Model Versions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{models.length}</div>
                <p className="text-xs text-gray-500 mt-1">
                  {models.filter((m: MLModel) => m.is_deployed).length} deployed
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Active Pipelines
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {pipelines.filter((p: DetectionPipeline) => p.is_active).length}
                </div>
                <p className="text-xs text-gray-500 mt-1">Out of {pipelines.length}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Avg Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">86%</div>
                <p className="text-xs text-gray-500 mt-1">F1-Score</p>
              </CardContent>
            </Card>
          </div>

          {/* Training Jobs */}
          <Card>
            <CardHeader>
              <CardTitle>Training Jobs</CardTitle>
            </CardHeader>
            <CardContent>
              {trainingJobs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Brain className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No training jobs yet. Create one to get started!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {trainingJobs.map((job: TrainingJob) => (
                    <div
                      key={job.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                      data-testid={`training-job-${job.id}`}
                    >
                      <div className="flex items-center space-x-4 flex-1">
                        {getStatusIcon(job.status)}
                        <div className="flex-1">
                          <div className="font-medium">
                            {job.service_name} / {job.metric_name}
                          </div>
                          <div className="text-sm text-gray-500">Model: {job.model_id}</div>
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        {job.status === "training" && (
                          <div className="text-center">
                            <div className="text-sm font-medium">{job.progress.toFixed(0)}%</div>
                            <div className="text-xs text-gray-500">
                              {job.epochs_completed}/{job.total_epochs} epochs
                            </div>
                          </div>
                        )}

                        {job.validation_metrics?.f1_score && (
                          <div className="text-center">
                            <div className="text-sm font-medium text-green-600">
                              {(job.validation_metrics.f1_score * 100).toFixed(1)}%
                            </div>
                            <div className="text-xs text-gray-500">F1-Score</div>
                          </div>
                        )}

                        <Badge className={getStatusColor(job.status)}>{job.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Model Versions */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Model Versions</CardTitle>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (models.length > 0 && models[0]) {
                      setSelectedModel(models[0]);
                      setDeployForm({ ...deployForm, versionId: models[0].id });
                      setShowDeployDialog(true);
                    }
                  }}
                  disabled={models.length === 0}
                >
                  <Rocket className="w-4 h-4 mr-2" />
                  Deploy Model
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {models.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No trained models yet. Complete a training job first!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {models.map((model: MLModel) => (
                    <div
                      key={model.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                      data-testid={`model-${model.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">
                            {model.service_name} / {model.metric_name}
                          </span>
                          <Badge variant="outline">v{model.version}</Badge>
                          <Badge>{model.ensemble_method.toUpperCase()}</Badge>
                          {model.is_deployed && (
                            <Badge className="bg-green-100 text-green-800">
                              <Zap className="w-3 h-3 mr-1" />
                              Deployed
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          Trained on {model.training_data_points} points •
                          {new Date(model.trained_at).toLocaleDateString()}
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        {Object.entries(model.training_metrics)
                          .slice(0, 3)
                          .map(([key, value]: [string, any]) => (
                            <div key={key} className="text-center">
                              <div className="text-sm font-medium">{(value * 100).toFixed(1)}%</div>
                              <div className="text-xs text-gray-500">{key}</div>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Active Pipelines */}
          <Card>
            <CardHeader>
              <CardTitle>Deployed Pipelines</CardTitle>
            </CardHeader>
            <CardContent>
              {pipelines.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Rocket className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No deployed pipelines. Deploy a model to start!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pipelines.map((pipeline: DetectionPipeline) => (
                    <div
                      key={pipeline.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                      data-testid={`pipeline-${pipeline.id}`}
                    >
                      <div className="flex items-center space-x-4 flex-1">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            pipeline.is_active ? "bg-green-500 animate-pulse" : "bg-gray-400"
                          }`}
                        />
                        <div className="flex-1">
                          <div className="font-medium">{pipeline.name}</div>
                          <div className="text-sm text-gray-500">
                            {pipeline.service_name} / {pipeline.metric_name}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <div className="text-sm font-medium">{pipeline.predictions_count}</div>
                          <div className="text-xs text-gray-500">Predictions</div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm font-medium text-red-600">
                            {pipeline.anomalies_detected}
                          </div>
                          <div className="text-xs text-gray-500">Anomalies</div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm font-medium text-green-600">
                            {(pipeline.performance_score * 100).toFixed(0)}%
                          </div>
                          <div className="text-xs text-gray-500">Score</div>
                        </div>
                        <Button
                          size="sm"
                          variant={pipeline.is_active ? "destructive" : "default"}
                          onClick={() => handleTogglePipeline(pipeline.id, pipeline.is_active)}
                        >
                          {pipeline.is_active ? "Deactivate" : "Activate"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* RAG Agents Tab */}
      {activeTab === "rag" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Total RAG Agents
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{ragAgents.length}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Active Agents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {ragAgents.filter((agent: RAGAgent) => agent.is_active).length}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Auto-Assign Agents
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {ragAgents.filter((agent: RAGAgent) => agent.auto_assign).length}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">RAG Agents Management</h2>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowExternalRAGStats(true)}
                variant="outline"
                className="flex items-center gap-2"
              >
                <BarChart3 className="h-4 w-4" />
                External Agent Stats
              </Button>
              <Button
                onClick={() => setShowExternalRAGForm(true)}
                className="flex items-center gap-2"
              >
                <Cloud className="h-4 w-4" />
                Create External Agent
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                Internal RAG Agents
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {ragAgents.map((agent: RAGAgent) => (
                  <div
                    key={agent.id}
                    className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-gray-50 hover:shadow-md transition-all duration-200"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleViewRAGAgent(agent);
                    }}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{agent.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{agent.description}</p>

                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>
                            Model: {agent.model_provider}/{agent.model_name}
                          </span>
                          <span>Categories: {agent.categories.length}</span>
                          <span>Knowledge Bases: {agent.knowledge_bases.length}</span>
                          <span>Incidents: {agent.incidents_processed}</span>
                          <span>Success Rate: {Math.round(agent.success_rate)}%</span>
                        </div>

                        <div className="flex gap-2 mt-2">
                          {agent.categories.map((category: string) => (
                            <Badge key={category} variant="secondary" className="text-xs">
                              {getCategoryName(category)}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div className="ml-4 flex flex-col items-end gap-2">
                        <div className="flex gap-2">
                          <Badge variant={agent.is_active ? "default" : "secondary"}>
                            {agent.is_active ? "Active" : "Inactive"}
                          </Badge>
                          {agent.auto_assign && (
                            <Badge variant="outline" className="text-green-600">
                              Auto-Assign
                            </Badge>
                          )}
                        </div>
                        <button
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleViewRAGAgent(agent);
                          }}
                        >
                          View stats & tune →
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {ragAgents.length === 0 && (
                  <div className="text-center py-12">
                    <Bot className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-sm font-medium text-gray-900 mb-2">No RAG agents</h3>
                    <p className="text-sm text-gray-500">
                      Create your first RAG agent to start automated incident analysis.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Knowledge Bases Tab */}
      {activeTab === "knowledge" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Knowledge Bases</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{knowledgeBases.length}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Categories Covered
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {new Set(knowledgeBases.map((kb) => kb.category)).size}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Active Knowledge Bases
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {knowledgeBases.filter((kb) => kb.is_active).length}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Knowledge Bases</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {knowledgeBases.map((kb) => (
                  <div
                    key={kb.id}
                    className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-gray-50 hover:shadow-md transition-all duration-200"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleViewKnowledgeBase(kb);
                    }}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{kb.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{kb.description}</p>

                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>Type: {kb.content_type}</span>
                          <span>Category: {getCategoryName(kb.category)}</span>
                          <span>Used: {kb.usage_count} times</span>
                          <span>Version: {kb.version}</span>
                        </div>

                        <div className="flex gap-1 mt-2">
                          {kb.tags.map((tag: string, index: number) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div className="ml-4 flex flex-col gap-2">
                        <Badge variant={kb.is_active ? "default" : "secondary"}>
                          {kb.is_active ? "Active" : "Inactive"}
                        </Badge>
                        <div className="flex flex-col gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-8"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleViewKnowledgeBase(kb);
                            }}
                          >
                            <FileText className="mr-1 h-3 w-3" />
                            Manage Docs
                          </Button>
                          <Button
                            size="sm"
                            className="text-xs h-8"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedKnowledgeBase(kb);
                              setShowDocumentManager(true);
                            }}
                          >
                            <Upload className="mr-1 h-3 w-3" />
                            Upload
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {knowledgeBases.length === 0 && (
                  <div className="text-center py-12">
                    <Database className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-sm font-medium text-gray-900 mb-2">No knowledge bases</h3>
                    <p className="text-sm text-gray-500">
                      Create knowledge bases to provide context for RAG agents.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Knowledge Base Detail Modal */}
      {showKnowledgeBaseDetail && selectedKnowledgeBase && (
        <Dialog open={showKnowledgeBaseDetail} onOpenChange={setShowKnowledgeBaseDetail}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedKnowledgeBase.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Knowledge Base Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Category</h4>
                  <p className="text-sm text-gray-600">
                    {selectedKnowledgeBase.category
                      ? getCategoryName(selectedKnowledgeBase.category)
                      : "Not specified"}
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Content Type</h4>
                  <p className="text-sm text-gray-600">
                    {selectedKnowledgeBase.content_type || "Unknown"}
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Usage Count</h4>
                  <p className="text-sm text-gray-600">
                    {selectedKnowledgeBase.usage_count || 0} times
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Version</h4>
                  <p className="text-sm text-gray-600">v{selectedKnowledgeBase.version || 1}</p>
                </div>
              </div>

              {/* API Status Indicator */}
              {!selectedKnowledgeBase.structured_content && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    📡 Loading detailed information... (Showing basic info from cache)
                  </p>
                </div>
              )}

              {/* Description */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-medium text-gray-900">Description</h4>
                  <Button size="sm" variant="outline" onClick={() => setEditingKB(!editingKB)}>
                    {editingKB ? "Cancel" : "Edit Details"}
                  </Button>
                </div>
                {editingKB ? (
                  <div className="space-y-3">
                    <textarea
                      value={selectedKnowledgeBase.description || ""}
                      onChange={(e) =>
                        setSelectedKnowledgeBase({
                          ...selectedKnowledgeBase,
                          description: e.target.value,
                        })
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 h-20"
                      placeholder="Update knowledge base description..."
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          handleUpdateKnowledgeBase({
                            description: selectedKnowledgeBase.description,
                          });
                          setEditingKB(false);
                        }}
                      >
                        Save Changes
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">{selectedKnowledgeBase.description}</p>
                )}
              </div>

              {/* Structured Content - Procedures */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-medium text-gray-900">
                    Procedures (
                    {(selectedKnowledgeBase.structured_content?.procedures || []).length})
                  </h4>
                  <Button onClick={() => setShowAddProcedure(true)} size="sm">
                    Add Procedure
                  </Button>
                </div>
                {selectedKnowledgeBase.structured_content?.procedures &&
                selectedKnowledgeBase.structured_content.procedures.length > 0 ? (
                  <div className="space-y-4">
                    {selectedKnowledgeBase.structured_content.procedures.map(
                      (procedure: any, index: number) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <h5 className="font-medium text-gray-900">
                            Step {procedure.step}: {procedure.title}
                          </h5>
                          {procedure.actions && procedure.actions.length > 0 ? (
                            <ul className="mt-2 space-y-1">
                              {procedure.actions.map((action: string, actionIndex: number) => (
                                <li key={actionIndex} className="text-sm text-gray-600 ml-4">
                                  • {action}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-gray-500 mt-2">
                              No actions defined for this procedure
                            </p>
                          )}
                        </div>
                      )
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-500">No procedures defined yet</p>
                    <Button onClick={() => setShowAddProcedure(true)} size="sm" className="mt-2">
                      Add First Procedure
                    </Button>
                  </div>
                )}
              </div>

              {/* Tags */}
              {selectedKnowledgeBase.tags && selectedKnowledgeBase.tags.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedKnowledgeBase.tags.map((tag: string, index: number) => (
                      <Badge key={index} variant="outline">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Procedure Modal */}
      {showAddProcedure && (
        <Dialog open={showAddProcedure} onOpenChange={setShowAddProcedure}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New Procedure</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Step Number</label>
                <input
                  type="number"
                  value={newProcedure.step}
                  onChange={(e) =>
                    setNewProcedure({ ...newProcedure, step: parseInt(e.target.value) })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Procedure Title
                </label>
                <input
                  type="text"
                  value={newProcedure.title}
                  onChange={(e) => setNewProcedure({ ...newProcedure, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="e.g., Initial System Check"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Actions</label>
                {newProcedure.actions.map((action, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={action}
                      onChange={(e) => {
                        const newActions = [...newProcedure.actions];
                        newActions[index] = e.target.value;
                        setNewProcedure({ ...newProcedure, actions: newActions });
                      }}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                      placeholder={`Action ${index + 1}`}
                    />
                    {newProcedure.actions.length > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newActions = newProcedure.actions.filter((_, i) => i !== index);
                          setNewProcedure({ ...newProcedure, actions: newActions });
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setNewProcedure({
                      ...newProcedure,
                      actions: [...newProcedure.actions, ""],
                    })
                  }
                >
                  Add Action
                </Button>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowAddProcedure(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddProcedure}>Add Procedure</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* RAG Agent Detail Modal */}
      {showRAGAgentDetail && selectedRAGAgent && (
        <Dialog open={showRAGAgentDetail} onOpenChange={setShowRAGAgentDetail}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>RAG Agent: {selectedRAGAgent.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Agent Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900">Incidents Processed</h4>
                  <p className="text-2xl font-bold text-blue-700">
                    {agentStats.incidents_processed || 0}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-medium text-green-900">Success Rate</h4>
                  <p className="text-2xl font-bold text-green-700">
                    {agentStats.success_rate || 0}%
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="font-medium text-purple-900">Recommendations</h4>
                  <p className="text-2xl font-bold text-purple-700">
                    {agentStats.total_recommendations || 0}
                  </p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h4 className="font-medium text-yellow-900">Learning Events</h4>
                  <p className="text-2xl font-bold text-yellow-700">
                    {agentStats.learning_events || 0}
                  </p>
                </div>
              </div>

              {/* Agent Configuration */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Model</h4>
                  <p className="text-sm text-gray-600">
                    {selectedRAGAgent.model_provider}/{selectedRAGAgent.model_name}
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Temperature</h4>
                  <p className="text-sm text-gray-600">{selectedRAGAgent.temperature}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Max Tokens</h4>
                  <p className="text-sm text-gray-600">{selectedRAGAgent.max_tokens}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Confidence Threshold</h4>
                  <p className="text-sm text-gray-600">{selectedRAGAgent.confidence_threshold}</p>
                </div>
              </div>

              {/* Categories */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  Incident Categories ({selectedRAGAgent.categories.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedRAGAgent.categories.map((category: string) => (
                    <Badge key={category} variant="secondary">
                      {getCategoryName(category)}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* System Prompt */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">System Prompt</h4>
                <textarea
                  value={selectedRAGAgent.system_prompt}
                  onChange={(e) =>
                    setSelectedRAGAgent({
                      ...selectedRAGAgent,
                      system_prompt: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 h-32"
                />
              </div>

              {/* Fine-tuning Controls */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium text-gray-900">Fine-tuning Controls</h4>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingAgent(!editingAgent)}
                  >
                    {editingAgent ? "Cancel Editing" : "Enable Editing"}
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Temperature
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="1"
                      value={selectedRAGAgent.temperature}
                      onChange={(e) =>
                        setSelectedRAGAgent({
                          ...selectedRAGAgent,
                          temperature: parseFloat(e.target.value),
                        })
                      }
                      disabled={!editingAgent}
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 ${!editingAgent ? "bg-gray-100" : ""}`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Controls randomness (0-1)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Tokens
                    </label>
                    <input
                      type="number"
                      min="100"
                      max="4000"
                      value={selectedRAGAgent.max_tokens}
                      onChange={(e) =>
                        setSelectedRAGAgent({
                          ...selectedRAGAgent,
                          max_tokens: parseInt(e.target.value),
                        })
                      }
                      disabled={!editingAgent}
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 ${!editingAgent ? "bg-gray-100" : ""}`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Max response length</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confidence Threshold
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="1"
                      value={selectedRAGAgent.confidence_threshold}
                      onChange={(e) =>
                        setSelectedRAGAgent({
                          ...selectedRAGAgent,
                          confidence_threshold: parseFloat(e.target.value),
                        })
                      }
                      disabled={!editingAgent}
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 ${!editingAgent ? "bg-gray-100" : ""}`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Min confidence to act</p>
                  </div>
                </div>

                {/* Model Selection for Testing Different LLMs */}
                <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <h5 className="font-medium text-gray-900 mb-2">Test Different LLM Models</h5>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Model Provider
                      </label>
                      <select
                        value={selectedRAGAgent.model_provider}
                        onChange={(e) =>
                          setSelectedRAGAgent({
                            ...selectedRAGAgent,
                            model_provider: e.target.value,
                          })
                        }
                        disabled={!editingAgent}
                        className={`w-full border border-gray-300 rounded-md px-3 py-2 ${!editingAgent ? "bg-gray-100" : ""}`}
                      >
                        <option value="openai">OpenAI</option>
                        <option value="anthropic">Anthropic</option>
                        <option value="gemini">Gemini</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Model Name
                      </label>
                      <select
                        value={selectedRAGAgent.model_name}
                        onChange={(e) =>
                          setSelectedRAGAgent({
                            ...selectedRAGAgent,
                            model_name: e.target.value,
                          })
                        }
                        disabled={!editingAgent}
                        className={`w-full border border-gray-300 rounded-md px-3 py-2 ${!editingAgent ? "bg-gray-100" : ""}`}
                      >
                        {selectedRAGAgent.model_provider === "openai" && (
                          <>
                            <option value="gpt-4o">GPT-4o (Latest)</option>
                            <option value="gpt-4o-mini">GPT-4o Mini</option>
                            <option value="gpt-4-turbo">GPT-4 Turbo</option>
                          </>
                        )}
                        {selectedRAGAgent.model_provider === "anthropic" && (
                          <>
                            <option value="claude-3-5-sonnet-20241022">
                              Claude 3.5 Sonnet (Latest)
                            </option>
                            <option value="claude-3-haiku-20240307">Claude 3 Haiku</option>
                          </>
                        )}
                        {selectedRAGAgent.model_provider === "gemini" && (
                          <>
                            <option value="gemini-1.5-pro-002">Gemini 1.5 Pro</option>
                            <option value="gemini-1.5-flash-002">Gemini 1.5 Flash</option>
                          </>
                        )}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* LLM Testing Section */}
              <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                <h4 className="font-medium text-blue-900 mb-3">🧪 Test LLM Response</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">
                      Test Prompt
                    </label>
                    <textarea
                      value={testPrompt}
                      onChange={(e) => setTestPrompt(e.target.value)}
                      placeholder="Enter a test prompt to see how the current model configuration responds..."
                      className="w-full border border-blue-300 rounded-md px-3 py-2 h-20"
                    />
                  </div>
                  <Button
                    onClick={handleTestLLM}
                    disabled={!testPrompt.trim() || testingLLM}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {testingLLM ? "Testing..." : "🚀 Test Current Configuration"}
                  </Button>

                  {testResult && (
                    <div className="mt-3 p-3 bg-white border border-blue-200 rounded-md">
                      <h5 className="font-medium text-blue-900 mb-2">Test Result:</h5>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap">{testResult}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Learning History */}
              {selectedRAGAgent.recent_learning && selectedRAGAgent.recent_learning.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Recent Learning Events</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedRAGAgent.recent_learning.map((event: any, index: number) => (
                      <div key={index} className="border border-gray-200 rounded p-2 text-sm">
                        <div className="flex justify-between">
                          <span
                            className={`font-medium ${event.action_taken === "validated" ? "text-green-600" : "text-red-600"}`}
                          >
                            {event.action_taken.toUpperCase()}
                          </span>
                          <span className="text-gray-500">
                            {new Date(event.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-gray-600">{event.human_feedback}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setTestPrompt("");
                      setTestResult("");
                    }}
                    size="sm"
                  >
                    Clear Test
                  </Button>
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowRAGAgentDetail(false);
                      setEditingAgent(false);
                      setTestPrompt("");
                      setTestResult("");
                    }}
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      handleUpdateRAGAgent({
                        system_prompt: selectedRAGAgent.system_prompt,
                        temperature: selectedRAGAgent.temperature,
                        max_tokens: selectedRAGAgent.max_tokens,
                        confidence_threshold: selectedRAGAgent.confidence_threshold,
                        model_provider: selectedRAGAgent.model_provider,
                        model_name: selectedRAGAgent.model_name,
                      });
                      setEditingAgent(false);
                    }}
                    disabled={!editingAgent}
                  >
                    💾 Save All Changes
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Document Manager Dialog */}
      <Dialog open={showDocumentManager} onOpenChange={setShowDocumentManager}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Document Management - {selectedKnowledgeBase?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedKnowledgeBase && (
            <DocumentManager
              knowledgeBaseId={selectedKnowledgeBase.id}
              knowledgeBaseName={selectedKnowledgeBase.name}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* External RAG Agent Form Dialog */}
      <Dialog open={showExternalRAGForm} onOpenChange={setShowExternalRAGForm}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cloud className="h-5 w-5" />
              Create External RAG Agent
            </DialogTitle>
          </DialogHeader>
          <ExternalRAGAgentForm
            onSubmit={handleCreateExternalRAGAgent}
            onCancel={() => setShowExternalRAGForm(false)}
            knowledgeBases={knowledgeBases}
            // categories={Object.keys(CATEGORY_LABELS)}
            isSubmitLoading={createExternalRAGAgentLoading}
            categories={categories
              .filter((cat) => cat.is_active)
              .sort((a, b) => a.display_order - b.display_order)
              .map((cat) => cat.category)}
          />
        </DialogContent>
      </Dialog>

      {/* External RAG Agent Stats Dialog */}
      <Dialog open={showExternalRAGStats} onOpenChange={setShowExternalRAGStats}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              External RAG Agents Statistics
            </DialogTitle>
          </DialogHeader>
          <ExternalRAGStats />
        </DialogContent>
      </Dialog>
      {/* Training Dialog */}
      <Dialog open={showTrainingDialog} onOpenChange={setShowTrainingDialog}>
        <DialogContent className="max-w-2xl" data-testid="training-dialog">
          <DialogHeader>
            <DialogTitle>Create Training Job</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium block mb-2">Service</label>
              <Select
                value={trainingForm.serviceName}
                onValueChange={(value) => setTrainingForm({ ...trainingForm, serviceName: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((service: Service) => (
                    <SelectItem key={service.id} value={service.name}>
                      {service.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium block mb-2">Metric</label>
              <Select
                value={trainingForm.metricName}
                onValueChange={(value) => setTrainingForm({ ...trainingForm, metricName: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {metrics.map((metric: string) => (
                    <SelectItem key={metric} value={metric}>
                      {metric}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium block mb-2">Ensemble Method</label>
              <Select
                value={trainingForm.ensembleMethod}
                onValueChange={(value) =>
                  setTrainingForm({ ...trainingForm, ensembleMethod: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ensembleMethods.map((method: { value: string; label: string }) => (
                    <SelectItem key={method.value} value={method.value}>
                      {method.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium block mb-2">Epochs</label>
                <input
                  type="number"
                  className="w-full p-2 border rounded"
                  value={trainingForm.epochs}
                  onChange={(e) =>
                    setTrainingForm({ ...trainingForm, epochs: parseInt(e.target.value) })
                  }
                  min="10"
                  max="200"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Training Data Points</label>
                <input
                  type="number"
                  className="w-full p-2 border rounded"
                  value={trainingForm.trainingDataPoints}
                  onChange={(e) =>
                    setTrainingForm({
                      ...trainingForm,
                      trainingDataPoints: parseInt(e.target.value),
                    })
                  }
                  min="100"
                  max="500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowTrainingDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateTrainingJob}
                disabled={!trainingForm.serviceName}
                data-testid="start-training-btn"
              >
                Start Training
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deploy Dialog */}
      <Dialog open={showDeployDialog} onOpenChange={setShowDeployDialog}>
        <DialogContent data-testid="deploy-dialog">
          <DialogHeader>
            <DialogTitle>Deploy Model</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium block mb-2">Model Version</label>
              <Select
                value={deployForm.versionId}
                onValueChange={(value) => setDeployForm({ ...deployForm, versionId: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent>
                  {models.map((model: MLModel) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.service_name}/{model.metric_name} - v{model.version} (
                      {model.ensemble_method})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium block mb-2">Pipeline Name</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                placeholder="e.g., production-api-gateway"
                value={deployForm.pipelineName}
                onChange={(e) => setDeployForm({ ...deployForm, pipelineName: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowDeployDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleDeployModel}
                disabled={!deployForm.versionId || !deployForm.pipelineName}
                data-testid="deploy-confirm-btn"
              >
                <Rocket className="w-4 h-4 mr-2" />
                Deploy
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* RAG Agent Creation Dialog */}
      <Dialog open={showRAGAgentDialog} onOpenChange={setShowRAGAgentDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create RAG Agent</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name</label>
              <input
                type="text"
                value={ragAgentForm.name}
                onChange={(e) => setRAGAgentForm({ ...ragAgentForm, name: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="e.g., Database Incident Specialist"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={ragAgentForm.description}
                onChange={(e) => setRAGAgentForm({ ...ragAgentForm, description: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 h-20"
                placeholder="Describe the agent's expertise and capabilities"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Incident Categories
              </label>
              <div className="grid grid-cols-2 gap-2">
                {categories
                  .filter((cat) => cat.is_active)
                  .sort((a, b) => a.display_order - b.display_order)
                  .map((category) => (
                    <label key={category.id} className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={ragAgentForm.categories.includes(category.category)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setRAGAgentForm({
                              ...ragAgentForm,
                              categories: [...ragAgentForm.categories, category.category],
                            });
                          } else {
                            setRAGAgentForm({
                              ...ragAgentForm,
                              categories: ragAgentForm.categories.filter(
                                (cat: string) => cat !== category.category
                              ),
                            });
                          }
                        }}
                        className="mr-2"
                      />
                      {category.name}
                    </label>
                  ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Knowledge Bases
              </label>
              <div className="space-y-1">
                {knowledgeBases.map((kb: KnowledgeBase) => (
                  <label key={kb.id} className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={ragAgentForm.knowledge_bases.includes(kb.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRAGAgentForm({
                            ...ragAgentForm,
                            knowledge_bases: [...ragAgentForm.knowledge_bases, kb.id],
                          });
                        } else {
                          setRAGAgentForm({
                            ...ragAgentForm,
                            knowledge_bases: ragAgentForm.knowledge_bases.filter(
                              (id) => id !== kb.id
                            ),
                          });
                        }
                      }}
                      className="mr-2"
                    />
                    {kb.name} ({getCategoryName(kb.category)})
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model Provider
                </label>
                <select
                  value={ragAgentForm.model_provider}
                  onChange={(e) =>
                    setRAGAgentForm({ ...ragAgentForm, model_provider: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                  <option value="gemini">Gemini</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                <input
                  type="text"
                  value={ragAgentForm.model_name}
                  onChange={(e) => setRAGAgentForm({ ...ragAgentForm, model_name: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="e.g., gpt-4o"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">System Prompt</label>
              <textarea
                value={ragAgentForm.system_prompt}
                onChange={(e) =>
                  setRAGAgentForm({ ...ragAgentForm, system_prompt: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 h-24"
                placeholder="Define the agent's role and behavior"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Tokens</label>
                <input
                  type="number"
                  value={ragAgentForm.max_tokens}
                  onChange={(e) =>
                    setRAGAgentForm({ ...ragAgentForm, max_tokens: parseInt(e.target.value) })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Temperature</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={ragAgentForm.temperature}
                  onChange={(e) =>
                    setRAGAgentForm({
                      ...ragAgentForm,
                      temperature: parseFloat(e.target.value),
                    })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confidence Threshold
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={ragAgentForm.confidence_threshold}
                  onChange={(e) =>
                    setRAGAgentForm({
                      ...ragAgentForm,
                      confidence_threshold: parseFloat(e.target.value),
                    })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={ragAgentForm.auto_assign}
                  onChange={(e) =>
                    setRAGAgentForm({ ...ragAgentForm, auto_assign: e.target.checked })
                  }
                  className="mr-2"
                />
                Auto-assign to new incidents
              </label>

              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={ragAgentForm.learning_enabled}
                  onChange={(e) =>
                    setRAGAgentForm({ ...ragAgentForm, learning_enabled: e.target.checked })
                  }
                  className="mr-2"
                />
                Enable learning from human feedback
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowRAGAgentDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateRAGAgent}>Create RAG Agent</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Knowledge Base Creation Dialog */}
      <Dialog
        open={showKnowledgeBaseDialog}
        onOpenChange={(open) => {
          setShowKnowledgeBaseDialog(open);
          if (!open) resetKnowledgeBaseForm();
        }}
      >
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Knowledge Base</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4">
            {/* Name Field */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                Name
                <span className="text-red-500 ml-1">*</span>
                {knowledgeBaseForm.name.trim().length >= 3 &&
                  knowledgeBaseForm.name.length <= 100 && (
                    <Check className="w-4 h-4 text-green-500 ml-2" />
                  )}
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={knowledgeBaseForm.name}
                  onChange={(e) => handleKbFieldChange("name", e.target.value)}
                  onBlur={() => setKbTouched({ ...kbTouched, name: true })}
                  className={`w-full border rounded-md px-3 py-2.5 pr-10 transition-colors ${
                    kbFormErrors.name
                      ? "border-red-500 bg-red-50 focus:border-red-600 focus:ring-red-200"
                      : knowledgeBaseForm.name.trim().length >= 3 &&
                          knowledgeBaseForm.name.length <= 100
                        ? "border-green-500 bg-green-50 focus:border-green-600 focus:ring-green-200"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                  } focus:outline-none focus:ring-2`}
                  placeholder="e.g., Database Troubleshooting SOP"
                  maxLength={100}
                />
                {kbFormErrors.name && (
                  <AlertCircle className="absolute right-3 top-3 w-5 h-5 text-red-500" />
                )}
              </div>
              <div className="flex justify-between items-start mt-1.5">
                {kbFormErrors.name ? (
                  <p className="text-red-600 text-xs flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {kbFormErrors.name}
                  </p>
                ) : (
                  <p className="text-gray-500 text-xs">Minimum 3 characters required</p>
                )}
                <p
                  className={`text-xs ${
                    knowledgeBaseForm.name.length > 90
                      ? "text-orange-600 font-medium"
                      : "text-gray-400"
                  }`}
                >
                  {knowledgeBaseForm.name.length}/100
                </p>
              </div>
            </div>

            {/* Description Field */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                Description
                <span className="text-red-500 ml-1">*</span>
                {knowledgeBaseForm.description.trim().length >= 10 &&
                  knowledgeBaseForm.description.length <= 500 && (
                    <Check className="w-4 h-4 text-green-500 ml-2" />
                  )}
              </label>
              <div className="relative">
                <textarea
                  value={knowledgeBaseForm.description}
                  onChange={(e) => handleKbFieldChange("description", e.target.value)}
                  onBlur={() => setKbTouched({ ...kbTouched, description: true })}
                  className={`w-full border rounded-md px-3 py-2.5 h-24 transition-colors resize-none ${
                    kbFormErrors.description
                      ? "border-red-500 bg-red-50 focus:border-red-600 focus:ring-red-200"
                      : knowledgeBaseForm.description.trim().length >= 10 &&
                          knowledgeBaseForm.description.length <= 500
                        ? "border-green-500 bg-green-50 focus:border-green-600 focus:ring-green-200"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                  } focus:outline-none focus:ring-2`}
                  placeholder="Describe the knowledge base content and use cases..."
                  maxLength={500}
                />
                {kbFormErrors.description && (
                  <AlertCircle className="absolute right-3 top-3 w-5 h-5 text-red-500" />
                )}
              </div>
              <div className="flex justify-between items-start mt-1.5">
                {kbFormErrors.description ? (
                  <p className="text-red-600 text-xs flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {kbFormErrors.description}
                  </p>
                ) : (
                  <p className="text-gray-500 text-xs">Minimum 10 characters required</p>
                )}
                <p
                  className={`text-xs ${
                    knowledgeBaseForm.description.length > 450
                      ? "text-orange-600 font-medium"
                      : "text-gray-400"
                  }`}
                >
                  {knowledgeBaseForm.description.length}/500
                </p>
              </div>
            </div>

            {/* Category Field */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                Category
                <span className="text-red-500 ml-1">*</span>
                {knowledgeBaseForm.category && <Check className="w-4 h-4 text-green-500 ml-2" />}
              </label>
              <div className="relative">
                <select
                  value={knowledgeBaseForm.category}
                  onChange={(e) => handleKbFieldChange("category", e.target.value)}
                  onBlur={() => setKbTouched({ ...kbTouched, category: true })}
                  className={`w-full border rounded-md px-3 py-2.5 transition-colors ${
                    kbFormErrors.category
                      ? "border-red-500 bg-red-50 focus:border-red-600 focus:ring-red-200"
                      : knowledgeBaseForm.category
                        ? "border-green-500 bg-green-50 focus:border-green-600 focus:ring-green-200"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                  } focus:outline-none focus:ring-2`}
                >
                  <option value="">Select Category</option>
                  {categories
                    .filter((cat) => cat.is_active)
                    .sort((a, b) => a.display_order - b.display_order)
                    .map((category) => (
                      <option key={category.id} value={category.category}>
                        {category.name}
                      </option>
                    ))}
                </select>
                {kbFormErrors.category && (
                  <AlertCircle className="absolute right-9 top-3 w-5 h-5 text-red-500" />
                )}
              </div>
              {kbFormErrors.category && (
                <p className="text-red-600 text-xs mt-1.5 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {kbFormErrors.category}
                </p>
              )}
            </div>

            {/* Content Type Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content Type
                <span className="text-red-500 ml-1">*</span>
              </label>
              <select
                value={knowledgeBaseForm.content_type}
                onChange={(e) => {
                  setKnowledgeBaseForm({ ...knowledgeBaseForm, content_type: e.target.value });
                  // Reset file upload when changing content type
                  if (e.target.value !== "document") {
                    setKbUploadedFile(null);
                    setKbFileError("");
                    const newErrors = { ...kbFormErrors };
                    delete newErrors.file;
                    setKbFormErrors(newErrors);
                  }
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-colors"
              >
                <option value="structured">Structured Content</option>
                <option value="document">Document Upload</option>
                <option value="sop">Standard Operating Procedure</option>
              </select>
            </div>

            {/* File Upload (shown only for document content type) */}
            {knowledgeBaseForm.content_type === "document" && (
              <div className="bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg p-4">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Document
                  <span className="text-red-500 ml-1">*</span>
                  {kbUploadedFile && <Check className="w-4 h-4 text-green-500 ml-2" />}
                </label>
                <div className="space-y-3">
                  <input
                    type="file"
                    onChange={handleKbFileUpload}
                    accept=".pdf,.doc,.docx,.txt,.md,.json"
                    className={`w-full border rounded-md px-3 py-2 text-sm bg-white transition-colors ${
                      kbFileError || kbFormErrors.file
                        ? "border-red-500"
                        : kbUploadedFile
                          ? "border-green-500"
                          : "border-gray-300"
                    } focus:outline-none focus:ring-2 focus:ring-blue-200`}
                  />

                  {kbUploadedFile && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-300 rounded-md">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-green-900 truncate">
                          {kbUploadedFile.name}
                        </p>
                        <p className="text-xs text-green-700">
                          Size: {(kbUploadedFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                  )}

                  {(kbFileError || kbFormErrors.file) && (
                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-300 rounded-md">
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{kbFileError || kbFormErrors.file}</p>
                    </div>
                  )}

                  <div className="flex items-start gap-2 text-xs text-gray-600">
                    <FileText className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Accepted formats:</p>
                      <p>PDF, DOC, DOCX, TXT, MD, JSON</p>
                      <p className="mt-1 font-medium">Maximum file size: 10MB</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Validation Summary */}
            {Object.keys(kbFormErrors).length > 0 && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-red-800 mb-2">
                      Please fix the following errors:
                    </h4>
                    <ul className="space-y-1">
                      {Object.entries(kbFormErrors).map(([field, error]) => (
                        <li key={field} className="text-sm text-red-700 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                          <span className="font-medium capitalize">{field}:</span> {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Form Progress Indicator */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-700">Form Completion</span>
                <span className="text-xs font-semibold text-blue-600">
                  {
                    [
                      knowledgeBaseForm.name.trim().length >= 3,
                      knowledgeBaseForm.description.trim().length >= 10,
                      knowledgeBaseForm.category !== "",
                      knowledgeBaseForm.content_type !== "document" || kbUploadedFile !== null,
                    ].filter(Boolean).length
                  }
                  /4
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${
                      ([
                        knowledgeBaseForm.name.trim().length >= 3,
                        knowledgeBaseForm.description.trim().length >= 10,
                        knowledgeBaseForm.category !== "",
                        knowledgeBaseForm.content_type !== "document" || kbUploadedFile !== null,
                      ].filter(Boolean).length /
                        4) *
                      100
                    }%`,
                  }}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowKnowledgeBaseDialog(false);
                  resetKnowledgeBaseForm();
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateKnowledgeBase}
                disabled={!isKbFormValid()}
                className={`flex-1 ${
                  !isKbFormValid()
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-blue-700 hover:shadow-lg"
                } transition-all`}
              >
                <Database className="w-4 h-4 mr-2" />
                Create Knowledge Base
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModelStudio;
